import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    nav: {
      menu: 'Menu',
      contact: 'Contact',
      close: 'Close',
      properties: 'Properties',
      services: 'Services',
      offMarket: 'Off-Market',
    },
    
    // Homepage
    home: {
      heroSubtitle: 'Geneva & Beyond',
      heroTitle: 'Find Your',
      heroTitleAccent: 'Sanctuary',
      heroDescription: 'Exceptional properties in Geneva\'s most prestigious locations.',
      buy: 'Buy',
      rent: 'Rent',
      locationPlaceholder: 'City, Region, Zip Code...',
      maxBudget: 'Max. Budget',
      purchaseBudget: 'Purchase Budget',
      monthlyBudget: 'Monthly Budget',
      minSurface: 'Min. Surface',
      anyBedrooms: 'Bedrooms',
      beds: 'Beds',
      allTypes: 'All Property Types',
      moreOptions: 'More Options',
      searchLocation: 'Location',
      searchPrice: 'Price',
      searchBeds: 'Beds',
      searchButton: 'Search',
      allLocations: 'All Locations',
      any: 'Any',
      featuredLabel: 'Featured',
      selectedProperties: 'Selected Properties',
      viewAll: 'View All',
      viewProperty: 'View Property',
      offMarketTitle: 'Off-Market Access',
      offMarketDescription: 'Some of the finest properties are never publicly listed. Gain access to our exclusive portfolio through a private consultation.',
      requestAccess: 'Request Access',
      servicesLabel: 'What We Offer',
      servicesTitle: 'Our Services',
      servicesIntro: 'We support every project with a tailored approach, from property selection through to closing.',
      learnMore: 'Learn More',
      boutiqueApproach: 'A boutique approach to luxury real estate, combining local expertise with global perspective',
      beginSearch: 'Begin Your Search',
      serviceAcquisition: 'Acquisition',
      serviceAcquisitionDesc: 'Targeted search, property selection, and personalized buying support.',
      serviceAcquisitionCta: 'Discover',
      serviceSale: 'Sale',
      serviceSaleDesc: 'Strategic positioning, go-to-market, and full sales process management.',
      serviceSaleCta: 'Contact us',
      serviceOffMarket: 'Off market',
      serviceOffMarketDesc: 'Access to exclusive, confidential opportunities not publicly listed.',
      serviceOffMarketCta: 'Request access',
      serviceAdvisory: 'Advisory',
      serviceAdvisoryDesc: 'Analysis, positioning, and guidance for informed real estate decisions.',
      serviceAdvisoryCta: 'Learn more',
    },
    
    // Properties page
    properties: {
      heroSubtitle: 'Private Collection',
      heroTitle: 'Some of the finest properties are never publicly listed',
      heroDescription: 'Gain access to our exclusive off-market portfolio. Discretion, exclusivity, and exceptional opportunities—by invitation.',
      requestPrivateAccess: 'Request Private Access',
      filter: 'Filter',
      property: 'property',
      properties: 'properties',
      allLocations: 'All Locations',
      allTypes: 'All Types',
      location: 'Location',
      type: 'Type',
      noPropertiesFound: 'No properties found',
      tryAdjustingFilters: 'Try adjusting your filters',
      clearFilters: 'Clear Filters',
      beds: 'beds',
      baths: 'baths',
    },
    
    // Property Detail
    propertyDetail: {
      back: 'Back to Properties',
      overview: 'Overview',
      features: 'Features',
      floorPlan: 'Floor Plan',
      requestBrochure: 'Request Brochure',
      propertyFeatures: 'Property Features',
      requestInfo: 'Request Information',
      interestedInProperty: 'Interested in this property? Get in touch with our team for more information or to arrange a viewing.',
      fullName: 'Full Name',
      email: 'Email',
      phone: 'Phone',
      message: 'Message',
      sendInquiry: 'Send Inquiry',
      sending: 'Sending...',
      thankYou: 'Thank you for your inquiry',
      wellBeInTouch: 'We\'ll be in touch shortly.',
      downloadBrochure: 'Download Brochure',
      requestBrochureTitle: 'Request Property Brochure',
      requestBrochureDesc: 'Enter your details to receive the full property brochure.',
      sendRequest: 'Send Request',
    },
    
    // Off-Market page
    offMarket: {
      heroSubtitle: 'Private Collection',
      heroTitle: 'Exclusive Off-Market Properties',
      heroDescription: 'Access our confidential portfolio of exceptional properties, available only to qualified buyers.',
      whyOffMarket: 'Why Off-Market?',
      discretion: 'Discretion',
      discretionDesc: 'Complete privacy for sellers and buyers throughout the transaction.',
      exclusivity: 'Exclusivity',
      exclusivityDesc: 'Access to properties before they reach the public market.',
      personalService: 'Personal Service',
      personalServiceDesc: 'Dedicated attention and tailored property matching.',
      applyForAccess: 'Apply for Access',
      applyDescription: 'Complete the form below to be considered for our off-market program. Our team will review your application and contact you within 48 hours.',
      personalInfo: 'Personal Information',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      phone: 'Phone',
      propertyPreferences: 'Property Preferences',
      budget: 'Budget Range',
      preferredLocations: 'Preferred Locations',
      selectLocations: 'Select preferred locations',
      propertyTypes: 'Property Types',
      selectTypes: 'Select property types',
      timeline: 'Timeline',
      selectTimeline: 'Select timeline',
      immediately: 'Immediately',
      within3Months: 'Within 3 months',
      within6Months: 'Within 6 months',
      within12Months: 'Within 12 months',
      justExploring: 'Just exploring',
      additionalInfo: 'Additional Information',
      submitApplication: 'Submit Application',
      submitting: 'Submitting...',
      applicationReceived: 'Application Received',
      applicationReceivedDesc: 'Thank you for your interest in our off-market program. Our team will review your application and contact you within 48 hours.',
    },
    
    // Services page
    services: {
      heroSubtitle: 'Our Expertise',
      heroTitle: 'Services',
      heroDescription: 'A comprehensive suite of real estate services, delivered with discretion and expertise.',
      enquire: 'Enquire',
      propertySales: 'Property Sales',
      propertySalesDesc: 'Expert guidance through every stage of selling your property. Our discrete approach and extensive network ensure optimal outcomes for discerning sellers.',
      buyerRepresentation: 'Buyer Representation',
      buyerRepresentationDesc: 'Personalized search and acquisition services tailored to your unique requirements. We provide exclusive access to properties that match your lifestyle and investment criteria.',
      investmentAdvisory: 'Investment Advisory',
      investmentAdvisoryDesc: 'Strategic real estate investment guidance leveraging deep market knowledge and financial expertise. We identify opportunities that align with your wealth objectives.',
      offMarketAccess: 'Off-Market Access',
      offMarketAccessDesc: 'Exclusive access to properties never publicly listed. Our relationships with property owners provide our clients unique opportunities in Geneva\'s most prestigious locations.',
      propertyValuation: 'Property Valuation',
      propertyValuationDesc: 'Comprehensive market analysis and property valuation services. Our expertise ensures accurate assessments for informed decision-making.',
    },
    
    // Contact page
    contact: {
      heroSubtitle: 'Get in Touch',
      heroTitle: 'Contact',
      heroDescription: 'We\'re here to help you find your perfect property or answer any questions.',
      getInTouch: 'Get in Touch',
      visitUs: 'Visit Us',
      callUs: 'Call Us',
      emailUs: 'Email Us',
      hours: 'Hours',
      weekdays: 'Monday - Friday',
      weekend: 'Weekend by appointment',
      sendMessage: 'Send a Message',
      firstName: 'First Name',
      lastName: 'Last Name',
      fullName: 'Full Name',
      email: 'Email',
      phone: 'Phone',
      phoneOptional: 'Phone (Optional)',
      subject: 'Subject',
      message: 'Message',
      send: 'Send Message',
      sending: 'Sending...',
      thankYou: 'Thank you for your message',
      wellBeInTouch: 'We\'ll be in touch shortly.',
    },
    
    // Footer
    footer: {
      tagline: 'Exceptional properties in Geneva\'s most prestigious locations.',
      navigation: 'Navigation',
      home: 'Home',
      properties: 'Properties',
      services: 'Services',
      contact: 'Contact',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      allRightsReserved: 'All rights reserved.',
    },
    
    // Common
    common: {
      featured: 'Featured',
      viewProperty: 'View Property',
    },

    // Public status ribbons (sold / reserved / rented only)
    propertyStatus: {
      sold: 'Sold',
      reserved: 'Reserved',
      rented: 'Rented',
    },
    
    // Property Card hover
    propertyCard: {
      description: 'Description',
      bedrooms: 'Bedrooms',
      bathrooms: 'Bathrooms',
      livingArea: 'Living Area',
      landArea: 'Land Area',
      rooms: 'Rooms',
    },

    // Valuation flow
    valuation: {
      confidentialLabel: 'Confidential Estimate',
      title: 'Want to sell your property?',
      subtitle: 'Receive a personalized estimate of your property',
      addressPlaceholder: 'Property address',
      ctaButton: 'Estimate my property',
      confidentialNote: 'Your information will remain strictly confidential',
      pageTitle: 'Estimate your property',
      pageSubtitle: 'Answer a few questions to receive a personalized estimate of your property',
      step1: 'Location',
      step2: 'Property Type',
      step3: 'Characteristics',
      step4: 'Timeline',
      step5: 'Contact',
      back: 'Back',
      next: 'Next',
      submit: 'Submit',
      submitting: 'Submitting...',
      successTitle: 'Request sent',
      successMessage: 'Thank you. We will contact you shortly with your personalized estimate.',
      backToHome: 'Back to home',
    },
  },
  
  fr: {
    // Navigation
    nav: {
      menu: 'Menu',
      contact: 'Contact',
      close: 'Fermer',
      properties: 'Propriétés',
      services: 'Services',
      offMarket: 'Off-Market',
    },
    
    // Homepage
    home: {
      heroSubtitle: 'Genève & Environs',
      heroTitle: 'Trouvez Votre',
      heroTitleAccent: 'Sanctuaire',
      heroDescription: 'Propriétés d\'exception dans les emplacements les plus prestigieux de Genève.',
      buy: 'Acheter',
      rent: 'Louer',
      locationPlaceholder: 'Ville, Région, Code Postal...',
      maxBudget: 'Budget Max.',
      purchaseBudget: 'Budget d\'achat',
      monthlyBudget: 'Budget mensuel',
      minSurface: 'Surface Min.',
      anyBedrooms: 'Chambres',
      beds: 'Ch.',
      allTypes: 'Tous les Types',
      moreOptions: 'Plus d\'Options',
      searchLocation: 'Lieu',
      searchPrice: 'Prix',
      searchBeds: 'Chambres',
      searchButton: 'Rechercher',
      allLocations: 'Tous les lieux',
      any: 'Tous',
      featuredLabel: 'En Vedette',
      selectedProperties: 'Propriétés Sélectionnées',
      viewAll: 'Voir Tout',
      viewProperty: 'Voir la Propriété',
      offMarketTitle: 'Accès Off-Market',
      offMarketDescription: 'Certaines des plus belles propriétés ne sont jamais publiées. Accédez à notre portefeuille exclusif via une consultation privée.',
      requestAccess: 'Demander l\'Accès',
      servicesLabel: 'Ce Que Nous Offrons',
      servicesTitle: 'Nos Services',
      servicesIntro: 'Nous accompagnons chaque projet avec une approche sur mesure, de la sélection du bien à la finalisation de la transaction.',
      learnMore: 'En Savoir Plus',
      boutiqueApproach: 'Une approche boutique de l\'immobilier de prestige, alliant expertise locale et vision internationale',
      beginSearch: 'Commencez votre recherche',
      serviceAcquisition: 'Acquisition',
      serviceAcquisitionDesc: 'Recherche ciblée, sélection de biens et accompagnement personnalisé à l\'achat.',
      serviceAcquisitionCta: 'Découvrir',
      serviceSale: 'Vente',
      serviceSaleDesc: 'Valorisation stratégique, mise en marché et gestion complète du processus de vente.',
      serviceSaleCta: 'Nous contacter',
      serviceOffMarket: 'Off market',
      serviceOffMarketDesc: 'Accès à des opportunités exclusives, confidentielles et non diffusées publiquement.',
      serviceOffMarketCta: 'Demander l\'accès',
      serviceAdvisory: 'Conseil',
      serviceAdvisoryDesc: 'Analyse, positionnement et accompagnement pour des décisions immobilières éclairées.',
      serviceAdvisoryCta: 'En savoir plus',
    },
    
    // Properties page
    properties: {
      heroSubtitle: 'Collection Privée',
      heroTitle: 'Certaines des plus belles propriétés ne sont jamais publiées',
      heroDescription: 'Accédez à notre portefeuille exclusif Off-Market. Discrétion, exclusivité et opportunités exceptionnelles—sur invitation.',
      requestPrivateAccess: 'Demander un Accès Privé',
      filter: 'Filtrer',
      property: 'propriété',
      properties: 'propriétés',
      allLocations: 'Tous les lieux',
      allTypes: 'Tous les types',
      location: 'Lieu',
      type: 'Type',
      noPropertiesFound: 'Aucune propriété trouvée',
      tryAdjustingFilters: 'Essayez d\'ajuster vos filtres',
      clearFilters: 'Effacer les Filtres',
      beds: 'chambres',
      baths: 'salles de bain',
    },
    
    // Property Detail
    propertyDetail: {
      back: 'Retour aux Propriétés',
      overview: 'Aperçu',
      features: 'Caractéristiques',
      floorPlan: 'Plan',
      requestBrochure: 'Demander la Brochure',
      propertyFeatures: 'Caractéristiques de la Propriété',
      requestInfo: 'Demander des Informations',
      interestedInProperty: 'Intéressé par cette propriété? Contactez notre équipe pour plus d\'informations ou pour organiser une visite.',
      fullName: 'Nom Complet',
      email: 'Email',
      phone: 'Téléphone',
      message: 'Message',
      sendInquiry: 'Envoyer la Demande',
      sending: 'Envoi en cours...',
      thankYou: 'Merci pour votre demande',
      wellBeInTouch: 'Nous vous contacterons bientôt.',
      downloadBrochure: 'Télécharger la Brochure',
      requestBrochureTitle: 'Demander la Brochure',
      requestBrochureDesc: 'Entrez vos coordonnées pour recevoir la brochure complète.',
      sendRequest: 'Envoyer la Demande',
    },
    
    // Off-Market page
    offMarket: {
      heroSubtitle: 'Collection Privée',
      heroTitle: 'Propriétés Exclusives Off-Market',
      heroDescription: 'Accédez à notre portefeuille confidentiel de propriétés exceptionnelles, disponibles uniquement pour les acheteurs qualifiés.',
      whyOffMarket: 'Pourquoi l\'Off-Market?',
      discretion: 'Discrétion',
      discretionDesc: 'Confidentialité totale pour les vendeurs et acheteurs tout au long de la transaction.',
      exclusivity: 'Exclusivité',
      exclusivityDesc: 'Accès aux propriétés avant leur mise sur le marché public.',
      personalService: 'Service Personnalisé',
      personalServiceDesc: 'Attention dédiée et sélection de propriétés sur mesure.',
      applyForAccess: 'Demander l\'Accès',
      applyDescription: 'Remplissez le formulaire ci-dessous pour être considéré pour notre programme Off-Market. Notre équipe examinera votre candidature et vous contactera sous 48 heures.',
      personalInfo: 'Informations Personnelles',
      firstName: 'Prénom',
      lastName: 'Nom',
      email: 'Email',
      phone: 'Téléphone',
      propertyPreferences: 'Préférences Immobilières',
      budget: 'Budget',
      preferredLocations: 'Lieux Préférés',
      selectLocations: 'Sélectionnez les lieux préférés',
      propertyTypes: 'Types de Propriétés',
      selectTypes: 'Sélectionnez les types',
      timeline: 'Délai',
      selectTimeline: 'Sélectionnez le délai',
      immediately: 'Immédiatement',
      within3Months: 'Dans les 3 mois',
      within6Months: 'Dans les 6 mois',
      within12Months: 'Dans les 12 mois',
      justExploring: 'Je m\'informe',
      additionalInfo: 'Informations Supplémentaires',
      submitApplication: 'Soumettre la Candidature',
      submitting: 'Envoi en cours...',
      applicationReceived: 'Candidature Reçue',
      applicationReceivedDesc: 'Merci de votre intérêt pour notre programme Off-Market. Notre équipe examinera votre candidature et vous contactera sous 48 heures.',
    },
    
    // Services page
    services: {
      heroSubtitle: 'Notre Expertise',
      heroTitle: 'Services',
      heroDescription: 'Une gamme complète de services immobiliers, fournis avec discrétion et expertise.',
      enquire: 'Renseignements',
      propertySales: 'Vente de Propriétés',
      propertySalesDesc: 'Accompagnement expert à chaque étape de la vente de votre bien. Notre approche discrète et notre vaste réseau garantissent des résultats optimaux pour les vendeurs exigeants.',
      buyerRepresentation: 'Représentation Acquéreur',
      buyerRepresentationDesc: 'Services de recherche et d\'acquisition personnalisés, adaptés à vos exigences uniques. Nous offrons un accès exclusif aux propriétés correspondant à votre style de vie et vos critères d\'investissement.',
      investmentAdvisory: 'Conseil en Investissement',
      investmentAdvisoryDesc: 'Conseil stratégique en investissement immobilier s\'appuyant sur une connaissance approfondie du marché et une expertise financière. Nous identifions les opportunités alignées avec vos objectifs patrimoniaux.',
      offMarketAccess: 'Accès Off-Market',
      offMarketAccessDesc: 'Accès exclusif aux propriétés jamais publiées. Nos relations avec les propriétaires offrent à nos clients des opportunités uniques dans les emplacements les plus prestigieux de Genève.',
      propertyValuation: 'Évaluation Immobilière',
      propertyValuationDesc: 'Services complets d\'analyse de marché et d\'évaluation immobilière. Notre expertise garantit des estimations précises pour une prise de décision éclairée.',
    },
    
    // Contact page
    contact: {
      heroSubtitle: 'Nous Contacter',
      heroTitle: 'Contact',
      heroDescription: 'Nous sommes là pour vous aider à trouver la propriété idéale ou répondre à vos questions.',
      getInTouch: 'Nous Contacter',
      visitUs: 'Nous Rendre Visite',
      callUs: 'Nous Appeler',
      emailUs: 'Nous Écrire',
      hours: 'Horaires',
      weekdays: 'Lundi - Vendredi',
      weekend: 'Week-end sur rendez-vous',
      sendMessage: 'Envoyer un Message',
      firstName: 'Prénom',
      lastName: 'Nom',
      fullName: 'Nom Complet',
      email: 'Email',
      phone: 'Téléphone',
      phoneOptional: 'Téléphone (Optionnel)',
      subject: 'Sujet',
      message: 'Message',
      send: 'Envoyer le Message',
      sending: 'Envoi en cours...',
      thankYou: 'Merci pour votre message',
      wellBeInTouch: 'Nous vous contacterons bientôt.',
    },
    
    // Footer
    footer: {
      tagline: 'Propriétés d\'exception dans les emplacements les plus prestigieux de Genève.',
      navigation: 'Navigation',
      home: 'Accueil',
      properties: 'Propriétés',
      services: 'Services',
      contact: 'Contact',
      legal: 'Mentions Légales',
      privacy: 'Politique de Confidentialité',
      terms: 'Conditions Générales',
      allRightsReserved: 'Tous droits réservés.',
    },
    
    // Common
    common: {
      featured: 'En Vedette',
      viewProperty: 'Voir la Propriété',
    },

    // Public status ribbons (sold / reserved / rented only)
    propertyStatus: {
      sold: 'Vendu',
      reserved: 'Réservé',
      rented: 'Loué',
    },
    
    // Property Card hover
    propertyCard: {
      description: 'Description',
      bedrooms: 'Chambres',
      bathrooms: 'Salles de bain',
      livingArea: 'Surface habitable',
      landArea: 'Terrain',
      rooms: 'Pièces',
    },

    // Valuation flow
    valuation: {
      confidentialLabel: 'Estimation confidentielle',
      title: 'Vous souhaitez vendre votre bien ?',
      subtitle: 'Recevez une estimation personnalisée de votre propriété',
      addressPlaceholder: 'Adresse de votre bien',
      ctaButton: 'Estimer mon bien',
      confidentialNote: 'Vos informations resteront strictement confidentielles',
      pageTitle: 'Estimez votre bien',
      pageSubtitle: 'Répondez à quelques questions pour recevoir une estimation personnalisée de votre propriété',
      step1: 'Localisation',
      step2: 'Type de bien',
      step3: 'Caractéristiques',
      step4: 'Calendrier',
      step5: 'Contact',
      back: 'Retour',
      next: 'Suivant',
      submit: 'Envoyer',
      submitting: 'Envoi en cours...',
      successTitle: 'Demande envoyée',
      successMessage: 'Merci. Nous vous contacterons rapidement avec votre estimation personnalisée.',
      backToHome: 'Retour à l\'accueil',
    },
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('byron-language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('byron-language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'fr' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
